/**
 * Comparación visual: anuncio Pisos.com (original) vs datos importados al modelo canónico (portal v2).
 * Pisos.com no permite incrustar la ficha en iframe; se abre en nueva ventana o popup.
 */
import { useState, useCallback } from "react";
import { useTheme } from "../components/ThemeProvider";
import { portalImportPisosAsync, mensajeErrorPortalImport, PISOS_IMPORT_TIMEOUT_MS } from "../services/portalImportApi";
import usePermiso from "../hooks/usePermiso";

const EJEMPLO_URL =
  "https://www.pisos.com/comprar/chalet-orba_centro_urbano-63396086279_106500/";

const IMG_PROXY_BASE =
  window.location.hostname === "localhost"
    ? "http://localhost:4000/api/img-proxy?url="
    : "https://vitalcasa-crm-production.up.railway.app/api/img-proxy?url=";

function proxyFoto(u) {
  if (!u || typeof u !== "string") return u;
  if (u.startsWith("data:")) return u;
  return IMG_PROXY_BASE + encodeURIComponent(u);
}

function fmtValor(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Sí" : "No";
  if (typeof v === "object") return JSON.stringify(v, null, 0);
  return String(v);
}

function fmtPrecio(n) {
  if (n == null || n === "") return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return String(n);
  return new Intl.NumberFormat("es-ES").format(x) + " €";
}

function fmtFechaHora(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function CompararPortalPisosV2() {
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const tx = t.textPrimary;
  const surface = t.bgCard;
  const { puede } = usePermiso();
  const verPagina = puede("cmp_pisos", "ver");
  const [url, setUrl] = useState(EJEMPLO_URL);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);

  const ejecutar = useCallback(async () => {
    setErr(null);
    setData(null);
    const u = url.trim();
    if (!u || !u.includes("pisos.com")) {
      setErr("Pega una URL completa de una ficha de pisos.com");
      return;
    }
    setLoading(true);
    try {
      const res = await portalImportPisosAsync({ url: u }, { timeoutMs: PISOS_IMPORT_TIMEOUT_MS });
      setData(res);
    } catch (e) {
      setErr(mensajeErrorPortalImport(e));
    } finally {
      setLoading(false);
    }
  }, [url]);

  const abrirOriginalNuevaPestaña = () => {
    const u = (data?.url_origen || url).trim();
    if (u) window.open(u, "_blank", "noopener,noreferrer");
  };

  const abrirOriginalPopup = () => {
    const u = (data?.url_origen || url).trim();
    if (!u) return;
    const w = Math.min(980, window.screen.width - 80);
    const h = Math.min(1100, window.screen.height - 80);
    const left = window.screenX + window.outerWidth - w - 24;
    const top = window.screenY + 40;
    window.open(
      u,
      "pisos_comparar",
      `noopener,noreferrer,width=${w},height=${h},left=${left},top=${top},scrollbars=yes`
    );
  };

  const snap = data?.snapshot || {};
  const snapKeys = Object.keys(snap).sort();
  const prov = data?.provenance || {};
  const extras = data?.portal_extras || {};
  const fotos = Array.isArray(data?.fotos_urls) ? data.fotos_urls : [];
  const caraPortal = extras.caracteristicas_portal || {};
  const descLen = snap.web_desc_larga_es ? String(snap.web_desc_larga_es).length : 0;

  const card = {
    border: `1px solid ${t.border}`,
    borderRadius: 12,
    background: surface,
    padding: 16,
    marginBottom: 16,
  };

  if (!verPagina) {
    return (
      <div style={{ padding: 24, color: t.textMuted, fontSize: 14 }}>
        No tienes permiso para ver Comparar Pisos v2.
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 24px 48px", maxWidth: 1280, margin: "0 auto" }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: tx, margin: "0 0 8px" }}>
        Comparar Pisos.com → import v2 (canónico)
      </h1>
      <p style={{ color: t.textMuted, fontSize: 14, margin: "0 0 20px", lineHeight: 1.5 }}>
        Importa una sola URL con el nuevo sistema y revisa campo a campo. El portal original no se puede
        incrustar aquí (bloqueo del sitio); usa el botón para abrirlo al lado de esta pantalla.
      </p>

      <div style={{ ...card, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
        <label style={{ flex: "1 1 280px", display: "block" }}>
          <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: t.textMuted, marginBottom: 6 }}>
            URL del anuncio (pisos.com)
          </span>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.pisos.com/comprar/..."
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 8,
              border: `1px solid ${t.border}`,
              background: isDark ? "rgba(0,0,0,0.25)" : "#fff",
              color: tx,
              fontSize: 14,
            }}
          />
        </label>
        <button
          type="button"
          onClick={ejecutar}
          disabled={loading}
          style={{
            padding: "10px 20px",
            borderRadius: 8,
            border: "none",
            fontWeight: 700,
            cursor: loading ? "wait" : "pointer",
            background: "linear-gradient(135deg, #0ea5e9, #6366f1)",
            color: "#fff",
            opacity: loading ? 0.75 : 1,
          }}
        >
          {loading ? "Scrapeando anuncio (1–3 min)…" : "Importar y mostrar"}
        </button>
        <button
          type="button"
          onClick={() => setUrl(EJEMPLO_URL)}
          style={{
            padding: "10px 14px",
            borderRadius: 8,
            border: `1px solid ${t.border}`,
            background: "transparent",
            color: t.textMuted,
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          URL ejemplo Orba
        </button>
      </div>

      {err && (
        <div
          style={{
            ...card,
            borderColor: "rgba(239,68,68,0.5)",
            background: isDark ? "rgba(239,68,68,0.12)" : "#fef2f2",
            color: "#b91c1c",
          }}
        >
          {err}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 380px)",
          gap: 20,
          alignItems: "start",
        }}
        className="comparar-pisos-grid"
      >
        <style>{`
          @media (max-width: 960px) {
            .comparar-pisos-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>

        {/* Columna datos CRM */}
        <div>
          {data && (
            <>
              <div style={card}>
                <h2 style={{ fontSize: 16, margin: "0 0 12px", color: tx }}>Resumen canónico</h2>
                <div style={{ display: "grid", gap: 8, fontSize: 14, color: tx }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                      gap: 10,
                      fontSize: 13,
                      padding: 10,
                      borderRadius: 8,
                      background: isDark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.04)",
                    }}
                  >
                    <div>
                      <span style={{ color: t.textMuted }}>Publicación en portal</span>
                      <div style={{ fontWeight: 600 }}>
                        {fmtFechaHora(data.fecha_publicacion_portal || data.fecha_publicacion_portal_iso)}
                      </div>
                      {data.fecha_publicacion_portal_iso && (
                        <div style={{ fontSize: 11, color: t.textMuted }}>ISO: {data.fecha_publicacion_portal_iso}</div>
                      )}
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>Primera importación CRM</span>
                      <div style={{ fontWeight: 600 }}>{fmtFechaHora(data.fecha_primera_importacion_crm)}</div>
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>Última importación CRM</span>
                      <div style={{ fontWeight: 600 }}>{fmtFechaHora(data.fecha_ultima_importacion_crm)}</div>
                    </div>
                  </div>
                  <div>
                    <span style={{ color: t.textMuted }}>Título / web_titulo</span>
                    <div style={{ fontWeight: 600 }}>{snap.titulo || snap.web_titulo || "—"}</div>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                    <div>
                      <span style={{ color: t.textMuted }}>Operación</span>
                      <div>{fmtValor(snap.operacion)}</div>
                    </div>
                    {snap.referencia_portal && (
                      <div>
                        <span style={{ color: t.textMuted }}>Ref. anuncio (referencia_portal)</span>
                        <div style={{ fontWeight: 600 }}>{fmtValor(snap.referencia_portal)}</div>
                      </div>
                    )}
                    {(snap.tipo_inmueble_nombre || snap.tipo_inmueble_id != null) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Tipo de propiedad</span>
                        <div>
                          {fmtValor(snap.tipo_inmueble_nombre)}
                          {snap.tipo_inmueble_id != null ? (
                            <span style={{ fontSize: 12, color: t.textMuted }}>
                              {" "}
                              (tipo_inmueble_id: {String(snap.tipo_inmueble_id)})
                            </span>
                          ) : null}
                        </div>
                      </div>
                    )}
                    {(snap.poblacion_nombre || snap.poblacion_id != null) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Población</span>
                        <div>
                          {fmtValor(snap.poblacion_nombre)}
                          {snap.poblacion_id != null ? (
                            <span style={{ fontSize: 12, color: t.textMuted }}>
                              {" "}
                              (poblacion_id: {String(snap.poblacion_id)})
                            </span>
                          ) : null}
                        </div>
                      </div>
                    )}
                    {(snap.orientacion_nombre || snap.orientacion_id != null) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Orientación</span>
                        <div>
                          {fmtValor(snap.orientacion_nombre)}
                          {snap.orientacion_id != null ? (
                            <span style={{ fontSize: 12, color: t.textMuted }}>
                              {" "}
                              (orientacion_id: {String(snap.orientacion_id)})
                            </span>
                          ) : null}
                        </div>
                      </div>
                    )}
                    {(snap.ub_distancia_mar != null || extras.distancia_playa_m != null) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Distancia playa / mar</span>
                        <div style={{ fontWeight: 600 }}>
                          {fmtValor(snap.ub_distancia_mar ?? extras.distancia_playa_m)} m
                        </div>
                      </div>
                    )}
                    {snap.num_balcones != null && (
                      <div>
                        <span style={{ color: t.textMuted }}>Balcones (num_balcones)</span>
                        <div style={{ fontWeight: 600 }}>{fmtValor(snap.num_balcones)}</div>
                      </div>
                    )}
                    {extras.adaptado_movilidad_reducida === true && (
                      <div>
                        <span style={{ color: t.textMuted }}>Adaptado movilidad reducida</span>
                        <div style={{ fontWeight: 600 }}>Sí</div>
                      </div>
                    )}
                    <div>
                      <span style={{ color: t.textMuted }}>Tipo anunciante</span>
                      <div style={{ fontWeight: 700 }}>{fmtValor(snap.tipo_anunciante)}</div>
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>Referencia portal</span>
                      <div style={{ fontWeight: 600 }}>{fmtValor(snap.referencia_portal)}</div>
                    </div>
                    {(extras.barrio_portal || extras.zona_portal) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Barrio / zona (portal)</span>
                        <div>
                          {fmtValor(extras.barrio_portal || extras.zona_portal)}
                          {extras.barrio_posiblemente_incorrecto && (
                            <div style={{ fontSize: 11, color: "#b45309", marginTop: 4 }}>
                              ⚠ {extras.barrio_posiblemente_incorrecto_motivo || "Zona posiblemente incorrecta"}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {extras.superficie_contradiction_alert && (
                      <div style={{ gridColumn: "1 / -1", padding: 8, background: "#fff7ed", borderRadius: 6, fontSize: 12 }}>
                        <strong>Superficies — revisar:</strong> {extras.superficie_contradiction_motivo}
                        {extras.m2_construidos_posiblemente_utiles && (
                          <div style={{ marginTop: 4 }}>Construidos portal podrían ser m² útiles (inferido).</div>
                        )}
                        {extras.parcela_es_construidos_mas_jardin && (
                          <div style={{ marginTop: 4 }}>Solar/parcela ≈ construida + jardín.</div>
                        )}
                      </div>
                    )}
                    {extras.piscina_portal_listada && (
                      <div>
                        <span style={{ color: t.textMuted }}>Piscina (portal sin tipo)</span>
                        <div style={{ fontWeight: 600 }}>Listada en portal — tipo no especificado</div>
                      </div>
                    )}
                    {(snap.cal_energetica || extras.antiguedad_portal || snap.ano_construccion
                      || snap.cal_energetica_consumo != null || snap.cal_energetica_emisiones != null
                      || extras.cal_energetica_consumo_letra || extras.cal_energetica_emisiones_letra) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Cert. energ. / antigüedad</span>
                        <div>
                          {[
                            snap.cal_energetica ? `Cert: ${snap.cal_energetica}` : null,
                            extras.cal_energetica_consumo_letra ? `Consumo letra: ${extras.cal_energetica_consumo_letra}` : null,
                            extras.cal_energetica_emisiones_letra ? `Emisiones letra: ${extras.cal_energetica_emisiones_letra}` : null,
                            snap.cal_energetica_consumo != null ? `Consumo: ${snap.cal_energetica_consumo} kWh/m²` : null,
                            snap.cal_energetica_emisiones != null ? `Emisiones: ${snap.cal_energetica_emisiones} kg CO₂/m²` : null,
                            extras.antiguedad_portal ? `Antigüedad: ${extras.antiguedad_portal}` : null,
                            snap.ano_construccion ? `Año est.: ${snap.ano_construccion}` : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </div>
                    )}
                    {extras.precio_m2_portal != null && (
                      <div>
                        <span style={{ color: t.textMuted }}>Precio / m² (portal)</span>
                        <div>{fmtPrecio(extras.precio_m2_portal)} / m²</div>
                      </div>
                    )}
                    <div>
                      <span style={{ color: t.textMuted }}>Precio venta (precio_venta)</span>
                      <div style={{ fontWeight: 700 }}>{fmtPrecio(snap.precio_venta)}</div>
                      {data.precio_euros_raw_enrich != null &&
                        (snap.precio_venta == null || snap.precio_venta === "") && (
                        <div style={{ fontSize: 11, color: t.textMuted }}>
                          precioEuros parser: {fmtPrecio(data.precio_euros_raw_enrich)}
                        </div>
                      )}
                    </div>
                    {(extras.jardin === true ||
                      extras.jardin === false ||
                      snap.m2_jardin != null ||
                      snap.m2_jardin_obs) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Jardín</span>
                        <div>
                          {extras.jardin === false
                            ? "No (ficha portal)"
                            : extras.jardin === true ||
                                snap.m2_jardin != null ||
                                snap.m2_jardin_obs
                              ? [
                                  "Sí",
                                  snap.m2_jardin != null ? `${snap.m2_jardin} m²` : null,
                                  snap.m2_jardin_obs || null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")
                              : "—"}
                        </div>
                      </div>
                    )}
                    <div>
                      <span style={{ color: t.textMuted }}>Teléfono (normalizado)</span>
                      <div style={{ fontWeight: 600 }}>{fmtValor(snap.telefono)}</div>
                    </div>
                    {snap.ub_vistas_id != null && (
                      <div>
                        <span style={{ color: t.textMuted }}>Vistas (ub_vistas_id)</span>
                        <div style={{ fontFamily: "monospace", fontSize: 13 }}>{String(snap.ub_vistas_id)}</div>
                      </div>
                    )}
                    <div>
                      <span style={{ color: t.textMuted }}>Dorm. / baños</span>
                      <div>
                        {fmtValor(snap.num_dormitorios)} / {fmtValor(snap.num_banos)}
                      </div>
                    </div>
                    {(snap.altura_planta != null && snap.altura_planta !== "") ||
                    snap.piso ? (
                      <div>
                        <span style={{ color: t.textMuted }}>Planta en edificio (altura_planta / piso)</span>
                        <div>
                          {fmtValor(snap.altura_planta)}
                          {snap.piso ? ` · ${snap.piso}` : ""}
                        </div>
                      </div>
                    ) : null}
                    {snap.eq_ascensor === true || snap.eq_ascensor === false ? (
                      <div>
                        <span style={{ color: t.textMuted }}>Ascensor (eq_ascensor)</span>
                        <div>{snap.eq_ascensor ? "Sí" : "No"}</div>
                      </div>
                    ) : null}
                    <div>
                      <span style={{ color: t.textMuted }}>m² út. / const. / parcela</span>
                      <div>
                        {fmtValor(snap.m2_utiles)} / {fmtValor(snap.m2_construidos)} /{" "}
                        {fmtValor(snap.m2_parcela)}
                      </div>
                    </div>
                  </div>
                  <div>
                    <span style={{ color: t.textMuted }}>Contacto (alias CRM)</span>
                    <div style={{ fontWeight: 600 }}>{fmtValor(snap.alias)}</div>
                    {(extras.contacto_nombre || extras.contacto_apellidos) && (
                      <div style={{ fontSize: 13, marginTop: 4 }}>
                        <span style={{ color: t.textMuted }}>Nombre / apellidos (heurístico)</span>
                        <div>
                          {fmtValor(extras.contacto_nombre)} {fmtValor(extras.contacto_apellidos)}
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <span style={{ color: t.textMuted }}>Dirección (portal)</span>
                    <div style={{ fontSize: 13 }}>
                      <strong>Completa:</strong> {extras.direccion_completa_portal || "—"}
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <strong>Canónico:</strong> {fmtValor(snap.nombre_via)}
                      {snap.numero ? `, nº ${snap.numero}` : ""}
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <strong>CP:</strong> {fmtValor(snap.codigo_postal)}
                    </div>
                    {extras.ubicacion_aprox_portal && (
                      <div style={{ marginTop: 6, fontSize: 12, color: t.textMuted }}>
                        Ubicación aprox. (municipio/zona, no se usa como <code>nombre_via</code>):{" "}
                        {extras.ubicacion_aprox_portal}
                      </div>
                    )}
                  </div>
                  <div>
                    <span style={{ color: t.textMuted }}>
                      Descripción (web_desc_larga_es) — {descLen.toLocaleString("es-ES")} caracteres
                    </span>
                    <div
                      style={{
                        maxHeight: "min(75vh, 900px)",
                        overflow: "auto",
                        lineHeight: 1.45,
                        fontSize: 13,
                        padding: 10,
                        borderRadius: 8,
                        background: isDark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.04)",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {snap.web_desc_larga_es || "—"}
                    </div>
                  </div>
                </div>
              </div>

              <div style={card}>
                <h2 style={{ fontSize: 16, margin: "0 0 12px", color: tx }}>
                  Snapshot JSON → columnas <code>propiedades</code>
                </h2>
                <p style={{ fontSize: 12, color: t.textMuted, margin: "0 0 10px" }}>
                  Origen de cada valor (trazabilidad): columna derecha pequeña.
                </p>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: t.textMuted, borderBottom: `1px solid ${t.border}` }}>
                        <th style={{ padding: "8px 6px" }}>Campo CRM</th>
                        <th style={{ padding: "8px 6px" }}>Valor</th>
                        <th style={{ padding: "8px 6px", width: "28%" }}>Fuente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {snapKeys.map((k) => (
                        <tr key={k} style={{ borderBottom: `1px solid ${t.border}` }}>
                          <td style={{ padding: "8px 6px", fontFamily: "monospace", color: t.accentBlue }}>
                            {k}
                          </td>
                          <td style={{ padding: "8px 6px", color: tx, wordBreak: "break-word" }}>
                            {fmtValor(snap[k])}
                          </td>
                          <td style={{ padding: "8px 6px", fontSize: 11, color: t.textMuted }}>
                            {prov[k] || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <details style={card}>
                <summary style={{ cursor: "pointer", fontWeight: 600, color: tx }}>
                  Características crudas del portal (JSON)
                </summary>
                <pre
                  style={{
                    marginTop: 12,
                    fontSize: 11,
                    overflow: "auto",
                    maxHeight: 280,
                    color: tx,
                    background: isDark ? "rgba(0,0,0,0.25)" : "#f8fafc",
                    padding: 12,
                    borderRadius: 8,
                  }}
                >
                  {JSON.stringify(caraPortal, null, 2)}
                </pre>
              </details>

              <details style={card}>
                <summary style={{ cursor: "pointer", fontWeight: 600, color: tx }}>
                  Extras portal (contacto, teléfono, etc.)
                </summary>
                <pre
                  style={{
                    marginTop: 12,
                    fontSize: 11,
                    overflow: "auto",
                    maxHeight: 220,
                    color: tx,
                    background: isDark ? "rgba(0,0,0,0.25)" : "#f8fafc",
                    padding: 12,
                    borderRadius: 8,
                  }}
                >
                  {JSON.stringify(
                    { ...extras, caracteristicas_portal: "(arriba)" },
                    null,
                    2
                  )}
                </pre>
              </details>

              {fotos.length > 0 && (
                <div style={card}>
                  <h2 style={{ fontSize: 16, margin: "0 0 12px", color: tx }}>
                    Fotos ({fotos.length})
                  </h2>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                      gap: 10,
                    }}
                  >
                    {fotos.slice(0, 24).map((src, i) => (
                      <a
                        key={i}
                        href={src}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: "block", borderRadius: 8, overflow: "hidden", border: `1px solid ${t.border}` }}
                      >
                        <img
                          src={proxyFoto(src)}
                          alt=""
                          style={{ width: "100%", height: 100, objectFit: "cover", display: "block" }}
                          loading="lazy"
                        />
                      </a>
                    ))}
                  </div>
                  {fotos.length > 24 && (
                    <p style={{ fontSize: 12, color: t.textMuted, margin: "10px 0 0" }}>
                      Mostrando 24 de {fotos.length}; el resto sigue en la respuesta API.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Columna anuncio original */}
        <div style={{ position: "sticky", top: 16 }}>
          <div style={card}>
            <h2 style={{ fontSize: 16, margin: "0 0 10px", color: tx }}>Anuncio original</h2>
            <p style={{ fontSize: 13, color: t.textMuted, lineHeight: 1.5, margin: "0 0 14px" }}>
              Coloca esta página a la izquierda y la ventana del portal a la derecha, o usa dos monitores.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                type="button"
                onClick={abrirOriginalNuevaPestaña}
                style={{
                  padding: "12px 16px",
                  borderRadius: 8,
                  border: "none",
                  fontWeight: 700,
                  cursor: "pointer",
                  background: "#ea580c",
                  color: "#fff",
                }}
              >
                Abrir Pisos.com en nueva pestaña
              </button>
              <button
                type="button"
                onClick={abrirOriginalPopup}
                style={{
                  padding: "12px 16px",
                  borderRadius: 8,
                  border: `1px solid ${t.border}`,
                  fontWeight: 600,
                  cursor: "pointer",
                  background: "transparent",
                  color: tx,
                }}
              >
                Abrir en ventana flotante
              </button>
            </div>
            {(data?.url_origen || url) && (
              <p
                style={{
                  fontSize: 11,
                  wordBreak: "break-all",
                  marginTop: 14,
                  color: t.textMuted,
                  fontFamily: "monospace",
                }}
              >
                {data?.url_origen || url}
              </p>
            )}
          </div>

          {data && (
            <div style={{ ...card, fontSize: 12, color: t.textMuted }}>
              <div>
                <strong style={{ color: tx }}>ID import:</strong> {data.id}
              </div>
              <div style={{ marginTop: 6 }}>
                <strong style={{ color: tx }}>id anuncio:</strong> {data.pisos_ad_id}
              </div>
              <div style={{ marginTop: 6 }}>
                <strong style={{ color: tx }}>Actualizado:</strong>{" "}
                {data.updated_at ? new Date(data.updated_at).toLocaleString("es-ES") : "—"}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
